import React from 'react'
import {CompatRoute, Navigate, useLocation} from 'react-router-dom-v5-compat'

import {useAuth} from '../../stores/AuthStore'
import {useReleaseFlags} from '../../hooks/useReleaseFlags'
import {FeatureName} from '../../types'
import {PageTemplate} from '../../templates/PageTemplate/PageTemplate'
import {LoadingState} from '../LoadingState/LoadingState'

type Props = React.ComponentProps<typeof CompatRoute> & {
  clientAuthorization?: FeatureName
  releaseRender?: {
    releaseFlag: FeatureName
    render: () => React.ReactNode
  }
}

export const AuthRoute: React.FC<React.PropsWithChildren<Props>> = ({
  clientAuthorization,
  releaseRender,
  compat,
  ...props
}) => {
  const location = useLocation()
  const {authenticationStatus, useIdpAuthentication} = useAuth()
  const {getIsReleasedToUser} = useReleaseFlags()

  if (authenticationStatus === 'unauthenticated') {
    return (
      <Navigate
        to='/login'
        state={{
          from: location,
        }}
        replace
      />
    )
  }

  // User is using Auth0 and we're in a incompleted auth state
  // when a new token is being exchanged we want to unmount all the normal route content so that when it reloads it refetches with the new token
  if (
    useIdpAuthentication &&
    (authenticationStatus === 'idp_authenticated' ||
      authenticationStatus === 'pending')
  ) {
    return (
      <PageTemplate width={['100%']} maxWidth={['1030px']} borderX={['0px']}>
        <LoadingState />
      </PageTemplate>
    )
  }

  if (clientAuthorization) {
    const isAuthorized = getIsReleasedToUser(clientAuthorization)

    if (!isAuthorized) {
      return <Navigate to='/home' replace />
    }
  }

  // Used to conditionally render a component based on a release flag.
  // To be used for temporary release flags only to ensure large view
  // changes can be verified in production first and possibly canary release
  const additionalProps: {
    render?: Props['render']
  } = {}

  if (releaseRender) {
    const isReleased = getIsReleasedToUser(releaseRender.releaseFlag)

    if (isReleased) {
      additionalProps.render = releaseRender.render
    }
  }

  return <CompatRoute {...props} {...additionalProps} />
}
